import { Box, Button, Container, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert } from "@mui/material";
import { ShoppingCart, Send } from "@mui/icons-material";
import { useState, useEffect } from "react";
import { bookingService } from "../services/bookingService";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

export default function PricingSection() {
  const [openCarnetModal, setOpenCarnetModal] = useState(false);
  const [openLessonModal, setOpenLessonModal] = useState(false);
  const [carnets, setCarnets] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    selectedCarnetId: "",
  });
  const [lessonFormData, setLessonFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    selectedLessonId: "",
  });
  const [loading, setLoading] = useState(false);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [lessonSuccess, setLessonSuccess] = useState(false);
  const [error, setError] = useState("");
  const [lessonError, setLessonError] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const [carnetsData, lessonTypesData] = await Promise.all([
          bookingService.getCarnets(),
          bookingService.getLessonTypes()
        ]);
        setCarnets(carnetsData);
        setLessonTypes(lessonTypesData);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    loadData();
  }, []);

  const groupLessons = lessonTypes.filter(lesson => lesson.isGroup);
  const individualLessons = lessonTypes.filter(lesson => !lesson.isGroup && lesson.price && lesson.price > 0);

  const handleLessonClick = () => {
    setOpenLessonModal(true);
  };

  const handleCarnetClick = () => {
    setOpenCarnetModal(true);
  };

  const handleCloseModal = () => {
    setOpenCarnetModal(false);
    setFormData({ firstName: "", lastName: "", email: "", selectedCarnetId: "" });
    setSuccess(false);
    setError("");
  };

  const handleCloseLessonModal = () => {
    setOpenLessonModal(false);
    setLessonFormData({ firstName: "", lastName: "", email: "", selectedLessonId: "" });
    setLessonSuccess(false);
    setLessonError("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLessonInputChange = (e) => {
    const { name, value } = e.target;
    setLessonFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitCarnetRequest = async () => {
    try {
      setLoading(true);
      setError("");

      // Validazione
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.selectedCarnetId) {
        setError("Tutti i campi sono obbligatori");
        setLoading(false);
        return;
      }

      const selectedCarnet = carnets.find(c => c.id === formData.selectedCarnetId);
      if (!selectedCarnet) {
        setError("Pacchetto selezionato non trovato");
        setLoading(false);
        return;
      }

      console.log("📧 Invio richiesta Pacchetto...");
      const sendCarnetRequest = httpsCallable(functions, "sendCarnetRequest");
      await sendCarnetRequest({
        userName: `${formData.firstName} ${formData.lastName}`,
        userEmail: formData.email,
        carnetName: selectedCarnet.name,
        carnetPrice: selectedCarnet.price,
      });

      console.log("✅ Richiesta Pacchetto inviata con successo");
      setSuccess(true);
      setFormData({ firstName: "", lastName: "", email: "", selectedCarnetId: "" });
    } catch (error) {
      console.error("❌ Errore nell'invio richiesta Pacchetto:", error);
      setError("Errore nell'invio della richiesta. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLessonRequest = async () => {
    try {
      setLessonLoading(true);
      setLessonError("");

      // Validazione
      if (!lessonFormData.firstName || !lessonFormData.lastName || !lessonFormData.email || !lessonFormData.selectedLessonId) {
        setLessonError("Tutti i campi sono obbligatori");
        setLessonLoading(false);
        return;
      }

      const selectedLesson = individualLessons.find(l => l.id === lessonFormData.selectedLessonId);
      if (!selectedLesson) {
        setLessonError("Lezione selezionata non trovata");
        setLessonLoading(false);
        return;
      }

      console.log("📧 Invio richiesta lezione singola...");
      const sendLessonRequest = httpsCallable(functions, "sendLessonRequest");
      await sendLessonRequest({
        userName: `${lessonFormData.firstName} ${lessonFormData.lastName}`,
        userEmail: lessonFormData.email,
        lessonName: selectedLesson.name,
        lessonPrice: selectedLesson.price || 0,
      });

      console.log("✅ Richiesta lezione inviata con successo");
      setLessonSuccess(true);
      setLessonFormData({ firstName: "", lastName: "", email: "", selectedLessonId: "" });
    } catch (error) {
      console.error("❌ Errore nell'invio richiesta lezione:", error);
      setLessonError("Errore nell'invio della richiesta. Riprova più tardi.");
    } finally {
      setLessonLoading(false);
    }
  };

  return (
    <Container id="prezzi" maxWidth="lg" sx={{ py: 8, scrollMarginTop: '10px' }}>
      <Typography
        variant="h3"
        component="h2"
        textAlign="center"
        gutterBottom
        sx={{
          fontWeight: "bold",
          color: "#145444",
          mb: 6
        }}
      >
        Prezzi e Abbonamenti
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 4 }}>
        {/* Carnet */}
        <Box
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: 3,
            p: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#145444", mb: 3 }}>
              Pacchetti
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {carnets.map((carnet) => (
                <Box key={carnet.id} component="li" sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body1">• {carnet.name}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#145444" }}>€{carnet.price || 0}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={handleCarnetClick}
              sx={{
                backgroundColor: "#145444",
                "&:hover": {
                  backgroundColor: "#0d3a2e",
                },
                px: 4,
                py: 1.5,
              }}
            >
              Acquista Ora
            </Button>
          </Box>
        </Box>

        {/* Lezioni Singole */}
        <Box
          sx={{
            backgroundColor: "#f9f9f9",
            borderRadius: 3,
            p: 4,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold", color: "#145444", mb: 3 }}>
              Lezioni Singole
            </Typography>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
              {individualLessons.map((lesson) => (
                <Box key={lesson.id} component="li" sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body1">• {lesson.name}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "#145444" }}>€{lesson.price || 0}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              onClick={handleLessonClick}
              sx={{
                backgroundColor: "#145444",
                "&:hover": {
                  backgroundColor: "#0d3a2e",
                },
                px: 4,
                py: 1.5,
              }}
            >
              Acquista Ora
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Modal Acquisto Carnet */}
      <Dialog open={openCarnetModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "#145444" }}>
          Richiesta Acquisto Pacchetto
        </DialogTitle>
        <DialogContent>
          {!success ? (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Compila il form qui sotto per ricevere le informazioni di pagamento via email.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Nome"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Cognome"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                select
                label="Seleziona Pacchetto"
                name="selectedCarnetId"
                value={formData.selectedCarnetId}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
                required
              >
                {carnets.map((carnet) => (
                  <MenuItem key={carnet.id} value={carnet.id}>
                    {carnet.name} - €{carnet.price}
                  </MenuItem>
                ))}
              </TextField>

              <Alert severity="info" sx={{ mb: 2 }}>
                Le informazioni per completare l'acquisto del Pacchetto ti verranno inviate via email.
              </Alert>
            </Box>
          ) : (
            <Alert severity="success" sx={{ mt: 2 }}>
              Richiesta inviata con successo! Controlla la tua email per le istruzioni di pagamento.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!success ? (
            <>
              <Button onClick={handleCloseModal} sx={{ color: "#145444" }}>
                Annulla
              </Button>
              <Button
                onClick={handleSubmitCarnetRequest}
                variant="contained"
                startIcon={<Send />}
                disabled={loading}
                sx={{
                  backgroundColor: "#145444",
                  "&:hover": { backgroundColor: "#0d3a2e" },
                }}
              >
                {loading ? "Invio..." : "Invia Richiesta"}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseModal} sx={{ color: "#145444" }}>
              Chiudi
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal Acquisto Lezione Singola */}
      <Dialog open={openLessonModal} onClose={handleCloseLessonModal} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: "bold", color: "#145444" }}>
          Richiesta Acquisto Lezione
        </DialogTitle>
        <DialogContent>
          {!lessonSuccess ? (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Compila il form qui sotto per ricevere le informazioni di pagamento via email.
              </Typography>

              {lessonError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {lessonError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Nome"
                name="firstName"
                value={lessonFormData.firstName}
                onChange={handleLessonInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Cognome"
                name="lastName"
                value={lessonFormData.lastName}
                onChange={handleLessonInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={lessonFormData.email}
                onChange={handleLessonInputChange}
                sx={{ mb: 2 }}
                required
              />

              <TextField
                fullWidth
                select
                label="Seleziona Lezione"
                name="selectedLessonId"
                value={lessonFormData.selectedLessonId}
                onChange={handleLessonInputChange}
                sx={{ mb: 2 }}
                required
              >
                {individualLessons.map((lesson) => (
                  <MenuItem key={lesson.id} value={lesson.id}>
                    {lesson.name} - €{lesson.price || 0}
                  </MenuItem>
                ))}
              </TextField>

              <Alert severity="info" sx={{ mb: 2 }}>
                Le informazioni per completare l'acquisto della lezione ti verranno inviate via email.
              </Alert>
            </Box>
          ) : (
            <Alert severity="success" sx={{ mt: 2 }}>
              Richiesta inviata con successo! Controlla la tua email per le istruzioni di pagamento.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!lessonSuccess ? (
            <>
              <Button onClick={handleCloseLessonModal} sx={{ color: "#145444" }}>
                Annulla
              </Button>
              <Button
                onClick={handleSubmitLessonRequest}
                variant="contained"
                startIcon={<Send />}
                disabled={lessonLoading}
                sx={{
                  backgroundColor: "#145444",
                  "&:hover": { backgroundColor: "#0d3a2e" },
                }}
              >
                {lessonLoading ? "Invio..." : "Invia Richiesta"}
              </Button>
            </>
          ) : (
            <Button onClick={handleCloseLessonModal} sx={{ color: "#145444" }}>
              Chiudi
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
